import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

/* ==================== CONFIGURATIONS ==================== */

const FIELD_OPTIONS = {
  Contact: ["Is", "Is Not", "Is Any", "Contains"],
  "Transaction Type": ["Is", "Is Not", "Is Any", "Is Empty", "Is Not Empty"],
  Stage: ["Is", "Is Not", "Is Any", "Is Empty", "Is Not Empty"],
  Associate: ["Is", "Is Not", "Is Any", "Is Empty", "Is Not Empty"],
  ContactTags: ["Is", "Is Not", "Is Any", "Is All", "Exclude Any", "Is Empty", "Is Not Empty"],
  "Last Contacted": ["On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days", "Is Empty", "Is Not Empty"],
  "Potential GCI": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
  "Potential Advisor Commission": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
};

const DROPDOWN_FIELDS = {
  Contact: { api: "contacts" },
  "Transaction Type": { api: "transaction_type" },
  Stage: { api: "stage" },
  Associate: { api: "users", roleFilter: 3 },
  ContactTags: { api: "contact_tags" },
};

const DATE_FIELDS = ["Last Contacted"];
const NUMERIC_FIELDS = ["Potential GCI", "Potential Advisor Commission"];

const PREFIX_MAP = {
  "Last Contacted": "last_contact_",
  "Potential GCI": "potential_gci_",
  "Potential Advisor Commission": "potential_advisor_commission_",
};

const JOIN_OPTIONS = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function PipelineAdvanceFilterData({ filterData, onSetFilterData, isSidebarOpen }) {
  const [config] = useAuth();
  const [groups, setGroups] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const fetchedFieldsRef = useRef(new Set());

  // Initialize groups
  useEffect(() => {
    if (isSidebarOpen) {
      setGroups(
        filterData?.length > 0
          ? filterData
          : [{ id: Date.now(), condition: "AND", filters: [{ join: null, field: "", operator: "", value: "" }] }]
      );
    }
  }, [isSidebarOpen, filterData]);

  // Fetch options when needed
  useEffect(() => {
    const fetchMissingOptions = async () => {
      const fieldsToFetch = new Set();

      groups.forEach((group) => {
        group.filters.forEach((filter) => {
          const field = filter.field;
          if (
            DROPDOWN_FIELDS[field] &&
            !dropdownOptions[field] &&
            !fetchedFieldsRef.current.has(field)
          ) {
            fieldsToFetch.add(field);
          }
        });
      });

      for (const field of fieldsToFetch) {
        fetchedFieldsRef.current.add(field);
        try {
          const options = await fetchDropdownOptions(field);
          setDropdownOptions((prev) => ({ ...prev, [field]: options }));
        } catch (err) {
          console.error(`Failed to load options for ${field}`, err);
          fetchedFieldsRef.current.delete(field);
        }
      }
    };

    if (isSidebarOpen && groups.length > 0) {
      fetchMissingOptions();
    }
  }, [groups, isSidebarOpen]);

  const updateState = (updated) => {
    setGroups(updated);
    onSetFilterData(updated);
  };

  /* ==================== FETCH OPTIONS ==================== */
  const fetchDropdownOptions = async (field, search = "") => {
    const configData = DROPDOWN_FIELDS[field];
    if (!configData) return [];

    try {
      let url = `${BASE_URL}/get-datalisting?list=${configData.api}&search=${search}`;
      if (configData.roleFilter) {
        url = `${BASE_URL}/get-datalisting?list=usersList&role_id=${configData.roleFilter}&search=${search}`;
      }

      const res = await axios.get(url, config);
      const data = res?.data?.data || {};

      switch (configData.api) {
        case "contacts":
          return data.contact_list?.map((el) => ({
            label: `${el.first_name} ${el.last_name}`,
            value: el.id,
          })) || [];

        case "transaction_type":
          return data.transaction_type?.map((el) => ({
            label: el.name,
            value: el.id,
          })) || [];

        case "stage":
          return data.stage?.map((el) => ({
            label: el.name,
            value: el.id,
          })) || [];

        case "users":
          return data.users?.map((el) => ({
            label: `${el.first_name} ${el.last_name}`,
            value: el.id,
          })) || [];

        case "contact_tags":
          return data.contact_tags?.map((el) => ({
            label: el.tag_name,
            value: el.id,
          })) || [];

        default:
          return [];
      }
    } catch (err) {
      if (err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message || "Failed to load options");
      }
      return [];
    }
  };

  /* ==================== HANDLERS ==================== */
  const addFilterRow = (groupIndex) => {
    const updated = [...groups];
    updated[groupIndex].filters.push({ join: "AND", field: "", operator: "", value: "" });
    updateState(updated);
  };

  const updateRow = (groupIndex, filterIndex, key, value) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];

    if (key === "field") {
      const newField = value?.value ?? value;
      filter.field = newField;
      filter.operator = "";
      filter.value = "";
    } else if (key === "operator") {
      const newOp = value?.value ?? value;
      filter.operator = newOp;
      const isMulti = ["Is Any", "Is All", "Exclude Any"].includes(newOp);

      if (DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field)) {
        const prefix = PREFIX_MAP[filter.field] || "";
        filter.value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        filter.value = isMulti ? [] : "";
      }
    } else {
      filter[key] = value?.value ?? value;
    }

    updateState(updated);
  };

  const handleDateChange = (groupIndex, filterIndex, subKey, subValue) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    filter.value = { ...filter.value || {}, [subKey]: subValue };
    updateState(updated);
  };

  const removeRow = (groupIndex, filterIndex) => {
    const updated = [...groups];
    if (updated[groupIndex].filters.length === 1) {
      if (updated.length > 1) {
        updated.splice(groupIndex, 1);
      } else {
        updated[groupIndex].filters[0] = { join: null, field: "", operator: "", value: "" };
      }
    } else {
      updated[groupIndex].filters.splice(filterIndex, 1);
    }
    updateState(updated);
  };

  /* ==================== RENDER VALUE INPUT ==================== */
  const renderValueInput = (groupIndex, filterIndex, filter) => {
    if (!filter.operator || ["Is Empty", "Is Not Empty"].includes(filter.operator)) return null;

    const isDate = DATE_FIELDS.includes(filter.field);
    const isNumeric = NUMERIC_FIELDS.includes(filter.field);
    if (isDate || isNumeric) return null;

    // ... (your existing dropdown and text input logic remains unchanged)
    if (DROPDOWN_FIELDS[filter.field]) {
      const isMulti = ["Is Any", "Is All", "Exclude Any"].includes(filter.operator);
      const isAsync = filter.field === "Contact";
      const SelectComponent = isAsync ? AsyncSelect : Select;

      if (isAsync) {
        return (
          <AsyncSelect
            className="flex-1 dark-H body-N"
            placeholder="Select Contact"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectPortalStyles}
            isSearchable
            isMulti={isMulti}
            loadOptions={(input) => fetchDropdownOptions(filter.field, input)}
            value={filter.value || null}
            onChange={(selected) => updateRow(groupIndex, filterIndex, "value", selected)}
            cacheOptions
            defaultOptions
          />
        );
      }

      const fieldOptions = dropdownOptions[filter.field] || [];

      return (
        <Select
          className="flex-1 dark-H body-N"
          placeholder="Select Value"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectPortalStyles}
          isSearchable
          isMulti={isMulti}
          options={fieldOptions}
          value={
            isMulti
              ? fieldOptions.filter((opt) =>
                  Array.isArray(filter.value) && filter.value.includes(opt.value)
                )
              : fieldOptions.find((opt) => opt.value === filter.value) || null
          }
          onChange={(selected) => {
            const newValue = isMulti
              ? selected?.map((opt) => opt.value) || []
              : selected?.value || "";
            updateRow(groupIndex, filterIndex, "value", newValue);
          }}
        />
      );
    }

    return (
      <input
        className="flex-1 border px-2 py-1 rounded dark-H body-N"
        placeholder="Enter Value"
        value={filter.value || ""}
        onChange={(e) => updateRow(groupIndex, filterIndex, "value", e.target.value)}
      />
    );
  };

  // New helper to decide what to show for Last Contacted
  const shouldShowSimpleDaysInput = (filter) => {
    return (
      filter.field === "Last Contacted" &&
      ["In Last X Days", "Longer Than X Days"].includes(filter.operator)
    );
  };

  return (
    <div className="space-y-6 text-black">
      {groups.map((group, groupIndex) => (
        <div key={group.id} className="bg-white space-y-3 p-4 rounded-lg">
          {groupIndex > 0 && (
            <div className="text-sm font-medium text-gray-600">{group.condition} Group</div>
          )}

          {group.filters.map((filter, index) => {
            const prefix = PREFIX_MAP[filter.field] || "";

            return (
              <div key={index} className="space-y-3">
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <Select
                      className="w-24 dark-H body-N"
                      options={JOIN_OPTIONS}
                      value={filter.join ? { label: filter.join, value: filter.join } : null}
                      onChange={(e) => updateRow(groupIndex, index, "join", e.value)}
                    />
                  )}

                  <Select
                    className="flex-1 dark-H body-N"
                    placeholder="Select Field"
                    isSearchable
                    options={Object.keys(FIELD_OPTIONS).map((f) => ({ label: f, value: f }))}
                    value={filter.field ? { label: filter.field, value: filter.field } : null}
                    onChange={(e) => updateRow(groupIndex, index, "field", e)}
                  />

                  <Select
                    className="flex-1 dark-H body-N"
                    placeholder="Condition"
                    isDisabled={!filter.field}
                    options={filter.field ? FIELD_OPTIONS[filter.field].map((c) => ({ label: c, value: c })) : []}
                    value={filter.operator ? { label: filter.operator, value: filter.operator } : null}
                    onChange={(e) => updateRow(groupIndex, index, "operator", e)}
                  />

                  {renderValueInput(groupIndex, index, filter)}

                  <button
                    className="text-red-500 hover:text-red-700 px-3 py-2"
                    onClick={() => removeRow(groupIndex, index)}
                  >
                    ✕
                  </button>
                </div>

                {/* Special Input for In Last X Days / Longer Than X Days */}
                {shouldShowSimpleDaysInput(filter) && (
                  <div className="pl-2">
                    <input
                      type="number"
                      className="dark-H body-N w-full max-w-[200px] border px-3 py-2 rounded"
                      placeholder="Enter number of days"
                      min={0}
                      value={filter.value?.[`${prefix}count`] || ""}
                      onChange={(e) =>
                        handleDateChange(groupIndex, index, `${prefix}count`, e.target.value)
                      }
                    />
                  </div>
                )}

                {/* Keep OptionalOption for all other date/numeric cases */}
                {filter.operator &&
                  !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                  !shouldShowSimpleDaysInput(filter) &&
                  (DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field)) && (
                    <div className="pl-2">
                      <OptionalOption
                        activeTab={
                          DATE_FIELDS.includes(filter.field)
                            ? filter.operator === "In Last X Days"
                              ? "within_last"
                              : filter.operator === "Longer Than X Days"
                              ? "longer_than"
                              : "date"
                            : "date"
                        }
                        type={NUMERIC_FIELDS.includes(filter.field) ? "number" : undefined}
                        onSetLastDate={(v) =>
                          handleDateChange(groupIndex, index, `${prefix}count`, v)
                        }
                        date={filter.value?.[`${prefix}count`] || ""}
                        onSetDateRange1={(v) =>
                          handleDateChange(groupIndex, index, `${prefix}start_count`, v)
                        }
                        date2={filter.value?.[`${prefix}start_count`] || ""}
                        onSetDateRange2={(v) =>
                          handleDateChange(groupIndex, index, `${prefix}end_count`, v)
                        }
                        date3={filter.value?.[`${prefix}end_count`] || ""}
                        lastUp_days={
                          (filter.operator === "In Last X Days" || filter.operator === "Longer Than X Days")
                            ? filter.value?.[`${prefix}count`]
                            : ""
                        }
                        onSetDays={(value) =>
                          handleDateChange(groupIndex, index, `${prefix}count`, value)
                        }
                        onClear={() => {
                          handleDateChange(groupIndex, index, `${prefix}count`, "");
                          handleDateChange(groupIndex, index, `${prefix}start_count`, "");
                          handleDateChange(groupIndex, index, `${prefix}end_count`, "");
                        }}
                      />
                    </div>
                  )}
              </div>
            );
          })}

          <button className="green-H head-5 mt-3" onClick={() => addFilterRow(groupIndex)}>
            + Add Filter
          </button>
        </div>
      ))}
    </div>
  );
}

export default PipelineAdvanceFilterData;